import React, { useCallback, useEffect, useMemo, useState } from 'react';
import CommissionTable from '../components/commissions/CommissionTable';
import CommissionModals from '../components/commissions/CommissionModals';
import {
  commissionApi,
  resolveCommissionApiError,
} from '../services/commissionApi';
import {
  computeCommission,
  formatCurrency,
  formatDate,
  matchesDateRange,
  normalizeRate,
  normalizeSearch,
  resolvePositionField,
  resolveCommissionStatus,
  resolveRoleLabel,
  todayIso,
  triggerCsvDownload,
  triggerExcelLikeDownload,
} from '../components/commissions/utils';
import useScopePermissions from '../hooks/useScopePermissions';
import { useAuth } from '../context/AuthContext';
import TablePagination, { usePagination } from '../components/TablePagination';
import './CommissionsPage.css';

const TAB_ITEMS = [
  { key: 'standard', label: 'Standard Commissions' },
  { key: 'autodeduct', label: 'Auto-Deducted Commissions' },
];

const FORM2307_PAYOR_DEFAULTS = {
  corpTin: '000-000-000-000',
  corpName: 'RMC2 CONSULTANCY',
  corpAddress: 'Head Office',
  corpZipcode: '',
  repName: 'AUTHORIZED REPRESENTATIVE',
  repPosition: 'AUTHORIZED SIGNATORY',
  repTin: '000-000-000-000',
};

function formatForm2307Date(value) {
  if (!value) {
    return '';
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return '';
  }

  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const year = String(date.getFullYear());
  return `${month}/${day}/${year}`;
}

function startOfQuarter(date) {
  const month = date.getMonth();
  const startMonth = Math.floor(month / 3) * 3;
  return new Date(date.getFullYear(), startMonth, 1);
}

function endOfQuarter(date) {
  const start = startOfQuarter(date);
  return new Date(start.getFullYear(), start.getMonth() + 3, 0);
}

function resolveAtcCode(transactionAmount, withholdingTax, transactionTag) {
  const amount = Number(transactionAmount || 0);
  const tax = Number(withholdingTax || 0);
  const tag = String(transactionTag || '').trim().toUpperCase();
  const ratio = tax > 0 ? amount / tax : 0;

  if (tag === 'HB') {
    return tax > 0 && Math.abs(ratio - 100) < 0.001 ? 'WC158' : 'WC160';
  }
  if (tax > 0 && Math.abs(ratio - 20) < 0.001) {
    return 'WI151';
  }
  return 'WI150';
}

function createDefaultPayForm() {
  return {
    reference_number: '',
    payment_date: todayIso(),
    proof_of_payment_name: '',
    proof_of_payment_url: '',
  };
}

function createDefaultReleaseForm() {
  return {
    release_reference: '',
    release_date: todayIso(),
  };
}

export default function CommissionsPage() {
  const { hasScope } = useScopePermissions();
  const { isAdmin, userBranchName } = useAuth();
  const canTagPaid = hasScope('commissions.tag-paid');
  const canCancel = hasScope('commissions.cancel');
  const canRelease = hasScope('commissions.release');

  const [collections, setCollections] = useState([]);
  const [sales, setSales] = useState([]);
  const [productCommissions, setProductCommissions] = useState([]);
  const [agents, setAgents] = useState([]);
  const [selectedBranch, setSelectedBranch] = useState('all');
  const [activeTab, setActiveTab] = useState('standard');
  const [search, setSearch] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedCollectionIds, setSelectedCollectionIds] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [payRow, setPayRow] = useState(null);
  const [payForm, setPayForm] = useState(createDefaultPayForm);
  const [cancelRow, setCancelRow] = useState(null);
  const [cancelRemarks, setCancelRemarks] = useState('');
  const [releaseDialog, setReleaseDialog] = useState(false);
  const [releaseForm, setReleaseForm] = useState(createDefaultReleaseForm);
  const [form2307Row, setForm2307Row] = useState(null);

  useEffect(() => {
    if (!isAdmin && userBranchName) {
      setSelectedBranch(userBranchName);
    }
  }, [isAdmin, userBranchName]);

  const loadData = useCallback(async (options = {}) => {
    const { clearFeedback = true } = options;

    setIsLoading(true);
    setError('');
    if (clearFeedback) {
      setMessage('');
    }

    try {
      const response = await commissionApi.listCommissions({ bustCache: true });
      const payload = response?.data || {};
      setCollections(Array.isArray(payload.collections) ? payload.collections : []);
      setSales(Array.isArray(payload.sales) ? payload.sales : []);
      setProductCommissions(Array.isArray(payload.product_commissions) ? payload.product_commissions : []);
      setAgents(Array.isArray(payload.agents) ? payload.agents : []);
    } catch (err) {
      setCollections([]);
      setSales([]);
      setProductCommissions([]);
      setAgents([]);
      setError(resolveCommissionApiError(err));
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const salesById = useMemo(() => {
    const map = new Map();
    sales.forEach((sale) => {
      map.set(Number(sale.id), sale);
    });
    return map;
  }, [sales]);

  const branchOptions = useMemo(() => {
    const names = new Set();
    collections.forEach((row) => {
      if (row?.branch) {
        names.add(row.branch);
      }
    });
    // Always surface the branch user's own branch so the filter never renders blank,
    // even before any commissionable collection has loaded for that branch.
    if (!isAdmin && userBranchName) {
      names.add(userBranchName);
    }
    return Array.from(names).sort((left, right) => left.localeCompare(right));
  }, [collections, isAdmin, userBranchName]);

  const commissionableCollections = useMemo(() => collections
    .filter((row) => Number(row.com_base || 0) > 0)
    // NAF earns no commission, so it never appears in the commission list.
    .filter((row) => String(row.commission_agent_role || '').toUpperCase() !== 'NAF')
    .filter((row) => selectedBranch === 'all' || String(row.branch || '') === selectedBranch), [collections, selectedBranch]);

  const paidCollections = useMemo(
    () => commissionableCollections.filter((row) => String(row.status || '') === 'Paid'),
    [commissionableCollections]
  );

  // AAH, AH, SAH, DH and CEO commissions are hidden from branch users; only an admin
  // processes those. SA/UM/BM remain visible to everyone.
  const ADMIN_ONLY_ROLES = useMemo(() => ['AAH', 'AH', 'SAH', 'DH', 'CEO'], []);

  function resolveRowPayout(collection, role) {
    const payouts = collection?.agent_payouts || {};
    const key = String(role || '').trim().toUpperCase();
    return payouts[key] || null;
  }

  // A row is "Released" once the bulk release runs on its collection. Otherwise it is
  // "Paid" when this specific agent has a payout record, else "Pending".
  function resolveRowStatus(row) {
    if (resolveCommissionStatus(row) === 'Released') {
      return 'Released';
    }
    if (String(row?.agent_payout?.status || '') === 'Paid') {
      return 'Paid';
    }
    return 'Pending';
  }

  const standardRows = useMemo(() => paidCollections.flatMap((collection) => {
    const sale = salesById.get(Number(collection.sale_id || 0));
    const commissionAgents = Array.isArray(sale?.commission_agents) ? sale.commission_agents : [];
    const standardAgents = commissionAgents.filter((agent) => !Boolean(agent.autodeduct));

    const buildRow = (role, agent, keySuffix) => {
      const payout = resolveRowPayout(collection, role);
      return {
        ...collection,
        row_key: `${collection.id}:${keySuffix}`,
        transaction_no: collection.transaction_no || sale?.transaction_no || null,
        commission_agent_role: role || null,
        agent_id: agent ? (agent.agent_id ?? agent.id ?? collection.agent_id) : collection.agent_id,
        agent_name: agent ? (agent.agent_name || collection.agent_name) : collection.agent_name,
        agent_position: resolveRoleLabel(role) || collection.agent_position || null,
        agent_payout: payout,
      };
    };

    let rows;
    if (standardAgents.length === 0) {
      rows = [buildRow(collection.commission_agent_role, null, 'primary')];
    } else {
      rows = standardAgents.map((agent, index) => buildRow(
        agent.role || collection.commission_agent_role,
        agent,
        `${agent.id || agent.role || index}:std`
      ));
    }

    if (!isAdmin) {
      rows = rows.filter((row) => !ADMIN_ONLY_ROLES.includes(String(row.commission_agent_role || '').toUpperCase()));
    }

    return rows;
  }), [paidCollections, salesById, isAdmin, ADMIN_ONLY_ROLES]);

  const autodeductRows = useMemo(() => paidCollections.flatMap((collection) => {
    const sale = salesById.get(Number(collection.sale_id || 0));
    const commissionAgents = Array.isArray(sale?.commission_agents) ? sale.commission_agents : [];

    let rows = commissionAgents
      .filter((agent) => Boolean(agent.autodeduct))
      .map((agent, index) => {
        const role = agent.role || collection.commission_agent_role;
        return {
          ...collection,
          row_key: `${collection.id}:${agent.id || agent.role || index}:auto`,
          transaction_no: collection.transaction_no || sale?.transaction_no || null,
          commission_agent_role: role || null,
          agent_id: agent.agent_id ?? agent.id ?? collection.agent_id,
          agent_name: agent.agent_name || collection.agent_name,
          agent_position: resolveRoleLabel(role) || collection.agent_position || null,
          agent_payout: resolveRowPayout(collection, role),
        };
      });

    if (!isAdmin) {
      rows = rows.filter((row) => !ADMIN_ONLY_ROLES.includes(String(row.commission_agent_role || '').toUpperCase()));
    }

    return rows;
  }), [paidCollections, salesById, isAdmin, ADMIN_ONLY_ROLES]);

  const resolveCommissionRate = useCallback((row) => {
    const productName = String(row.product || '').trim().toLowerCase();
    if (!productName) {
      return 0;
    }

    const product = productCommissions.find((item) => String(item.product_name || '').trim().toLowerCase() === productName);
    if (!product) {
      return 0;
    }

    let position = String(row.agent_position || '').trim();
    if (!position && row.commission_agent_role) {
      position = resolveRoleLabel(row.commission_agent_role);
    }
    if (!position && row.agent_name) {
      const matchedAgent = agents.find((agent) => String(agent.full_name || '').trim().toLowerCase() === String(row.agent_name || '').trim().toLowerCase());
      if (matchedAgent?.position) {
        position = String(matchedAgent.position);
      }
    }

    const field = resolvePositionField(position);
    if (!field) {
      return 0;
    }

    return normalizeRate(product[field]);
  }, [productCommissions, agents]);

  const applyFilters = useCallback((rows) => {
    const searchKey = normalizeSearch(search);

    return rows
      .filter((row) => {
        if (!searchKey) {
          return true;
        }

        const searchable = [
          row.transaction_no,
          row.agent_name,
          row.agent_position,
          row.client_name,
          row.product,
          row.branch,
          row.ar_si_no,
          row.payment_reference,
        ]
          .map((value) => normalizeSearch(value))
          .join(' ');

        return searchable.includes(searchKey);
      })
      .filter((row) => matchesDateRange(row.payment_date, dateFrom, dateTo))
      .filter((row) => {
        if (statusFilter === 'all') {
          return true;
        }
        // Per-row status: Released (bulk) > Paid (this agent tagged) > Pending.
        let currentStatus = resolveCommissionStatus(row);
        if (currentStatus !== 'Released' && String(row?.agent_payout?.status || '') === 'Paid') {
          currentStatus = 'Paid';
        }
        return currentStatus === statusFilter;
      });
  }, [search, dateFrom, dateTo, statusFilter]);

  const filteredStandardRows = useMemo(
    () => applyFilters(standardRows),
    [standardRows, applyFilters]
  );
  const filteredAutodeductRows = useMemo(
    () => applyFilters(autodeductRows),
    [autodeductRows, applyFilters]
  );

  const summary = useMemo(() => {
    const totalGross = commissionableCollections.reduce((sum, row) => sum + Number(row.gross_amount || 0), 0);
    const totalWtax = commissionableCollections.reduce((sum, row) => sum + Number(row.wtax || 0), 0);
    const totalNet = commissionableCollections.reduce((sum, row) => sum + Number(row.net_commission || 0), 0);
    const releasedRows = commissionableCollections.filter((row) => resolveCommissionStatus(row) === 'Released');
    const pendingRows = commissionableCollections.filter((row) => resolveCommissionStatus(row) !== 'Released');

    return {
      totalGross,
      totalWtax,
      totalNet,
      totalReleased: releasedRows.reduce((sum, row) => sum + Number(row.net_commission || 0), 0),
      totalPending: pendingRows.reduce((sum, row) => sum + Number(row.net_commission || 0), 0),
    };
  }, [commissionableCollections]);

  const pendingCollectionIds = useMemo(() => {
    const ids = new Set();
    filteredStandardRows.forEach((row) => {
      const status = resolveCommissionStatus(row);
      if (status !== 'Released' && String(row.status || '') !== 'Cancelled') {
        ids.add(Number(row.id));
      }
    });
    return Array.from(ids);
  }, [filteredStandardRows]);

  const pendingCollectionKey = useMemo(
    () => pendingCollectionIds.join(','),
    [pendingCollectionIds]
  );

  useEffect(() => {
    setSelectedCollectionIds((previous) => {
      if (!canRelease) {
        if (previous.length === 0) {
          return previous;
        }
        return [];
      }

      const next = previous.filter((id) => pendingCollectionIds.includes(id));
      if (next.length === previous.length) {
        return previous;
      }
      return next;
    });
  }, [pendingCollectionKey, pendingCollectionIds, canRelease]);

  const allPendingSelected = useMemo(() => {
    if (pendingCollectionIds.length === 0) {
      return false;
    }
    return pendingCollectionIds.every((id) => selectedCollectionIds.includes(id));
  }, [pendingCollectionIds, selectedCollectionIds]);

  const activeRows = activeTab === 'standard' ? filteredStandardRows : filteredAutodeductRows;

  const pagination = usePagination(activeRows);

  function buildExportRows(rows, tabKey) {
    return rows.map((row) => {
      const rate = resolveCommissionRate(row);
      const calculations = computeCommission(row.premium_amount, rate);
      const status = tabKey === 'autodeduct'
        ? 'Auto Deduct'
        : resolveCommissionStatus(row);

      return [
        formatDate(row.payment_date),
        row.transaction_no || '',
        row.ar_si_no || '',
        row.agent_name || '',
        row.agent_position || '',
        row.client_name || '',
        row.product || '',
        row.branch || '',
        calculations.premium.toFixed(2),
        `${(rate * 100).toFixed(2)}%`,
        calculations.vat.toFixed(2),
        calculations.netCommissionable.toFixed(2),
        calculations.grossCommission.toFixed(2),
        calculations.wtax.toFixed(2),
        calculations.netCommission.toFixed(2),
        status,
      ];
    });
  }

  function exportCsv() {
    const rows = buildExportRows(activeRows, activeTab);
    if (rows.length === 0) {
      return;
    }

    triggerCsvDownload(
      `commissions_${activeTab}_${todayIso()}.csv`,
      [
        'Payment Date',
        'Transaction No.',
        'AR / SI No.',
        'Agent',
        'Agent Position',
        'Client',
        'Product',
        'Branch',
        'Premium Paid',
        'Commission Rate',
        '12% VAT',
        'Net Commissionable',
        'Gross Commission',
        '10% WHT',
        'Net Commission',
        'Status',
      ],
      rows
    );
  }

  function exportExcel() {
    const rows = buildExportRows(activeRows, activeTab);
    if (rows.length === 0) {
      return;
    }

    triggerExcelLikeDownload(
      `commissions_${activeTab}_${todayIso()}.xls`,
      [
        'Payment Date',
        'Transaction No.',
        'AR / SI No.',
        'Agent',
        'Agent Position',
        'Client',
        'Product',
        'Branch',
        'Premium Paid',
        'Commission Rate',
        '12% VAT',
        'Net Commissionable',
        'Gross Commission',
        '10% WHT',
        'Net Commission',
        'Status',
      ],
      rows
    );
  }

  async function runSubmission(action, successMessage) {
    setIsSubmitting(true);
    setError('');
    setMessage('');

    try {
      await action();
      setMessage(successMessage);
      await loadData({ clearFeedback: false });
      return true;
    } catch (err) {
      setError(resolveCommissionApiError(err));
      return false;
    } finally {
      setIsSubmitting(false);
    }
  }

  async function submitTagPaid() {
    if (!canTagPaid) {
      setError('You do not have permission to tag commissions as paid.');
      return;
    }

    const referenceNumber = String(payForm.reference_number || '').trim();
    if (!payRow || !referenceNumber || !payForm.payment_date) {
      return;
    }

    const success = await runSubmission(
      () => commissionApi.tagCommissionPaid({
        collection_id: payRow.id,
        role: payRow.commission_agent_role,
        reference_number: referenceNumber,
        payment_date: payForm.payment_date,
        proof_of_payment_name: payForm.proof_of_payment_name || null,
        proof_of_payment_url: payForm.proof_of_payment_url || null,
      }),
      'Commission tagged as paid successfully.'
    );

    if (success) {
      setPayRow(null);
      setPayForm(createDefaultPayForm());
    }
  }

  async function handleUploadCommissionProof(file) {
    const response = await commissionApi.uploadProofOfPayment(file);
    return response?.data?.proof_of_payment || null;
  }

  async function submitCancelCommission() {
    if (!canCancel) {
      setError('You do not have permission to cancel commissions.');
      return;
    }

    const remarks = String(cancelRemarks || '').trim();
    if (!cancelRow || !remarks) {
      return;
    }

    const success = await runSubmission(
      () => commissionApi.cancelCommission({
        collection_id: cancelRow.id,
        cancellation_remarks: remarks,
      }),
      'Commission cancelled successfully.'
    );

    if (success) {
      setCancelRow(null);
      setCancelRemarks('');
    }
  }

  async function submitBulkRelease() {
    if (!canRelease) {
      setError('You do not have permission to release commissions.');
      return;
    }

    const releaseReference = String(releaseForm.release_reference || '').trim();
    if (selectedCollectionIds.length === 0 || !releaseReference || !releaseForm.release_date) {
      return;
    }

    const success = await runSubmission(
      () => commissionApi.releaseCommissions({
        collection_ids: selectedCollectionIds,
        release_reference: releaseReference,
        release_date: releaseForm.release_date,
      }),
      'Commission release completed successfully.'
    );

    if (success) {
      setReleaseDialog(false);
      setSelectedCollectionIds([]);
      setReleaseForm(createDefaultReleaseForm());
    }
  }

  function toggleRowSelection(collectionId) {
    if (!canRelease) {
      return;
    }

    const normalizedId = Number(collectionId);
    setSelectedCollectionIds((previous) => (
      previous.includes(normalizedId)
        ? previous.filter((id) => id !== normalizedId)
        : [...previous, normalizedId]
    ));
  }

  function toggleAllPendingSelection() {
    if (!canRelease) {
      return;
    }

    if (allPendingSelected) {
      setSelectedCollectionIds([]);
      return;
    }
    setSelectedCollectionIds(pendingCollectionIds);
  }

  function clearFilters() {
    setSearch('');
    setDateFrom('');
    setDateTo('');
    setStatusFilter('all');
  }

  function updatePayForm(field, value) {
    setPayForm((previous) => ({
      ...previous,
      [field]: value,
    }));
  }

  function updateReleaseForm(field, value) {
    setReleaseForm((previous) => ({
      ...previous,
      [field]: value,
    }));
  }

  const releaseTotal = useMemo(() => {
    const uniqueRows = [];
    const seen = new Set();

    standardRows.forEach((row) => {
      const collectionId = Number(row.id);
      if (!selectedCollectionIds.includes(collectionId) || seen.has(collectionId)) {
        return;
      }
      seen.add(collectionId);
      uniqueRows.push(row);
    });

    return uniqueRows.reduce((sum, row) => {
      const rate = resolveCommissionRate(row);
      const calculations = computeCommission(row.premium_amount, rate);
      return sum + calculations.netCommission;
    }, 0);
  }, [standardRows, selectedCollectionIds, resolveCommissionRate]);

  const form2307Data = useMemo(() => {
    if (!form2307Row) {
      return null;
    }

    const rate = resolveCommissionRate(form2307Row);
    const calculations = computeCommission(form2307Row.premium_amount, rate);
    const paymentDate = form2307Row.payment_date ? new Date(form2307Row.payment_date) : null;
    const hasValidPaymentDate = paymentDate instanceof Date && !Number.isNaN(paymentDate.getTime());
    const quarterStart = hasValidPaymentDate ? startOfQuarter(paymentDate) : null;
    const quarterEnd = hasValidPaymentDate ? endOfQuarter(paymentDate) : null;
    const monthOnly = hasValidPaymentDate ? paymentDate.getMonth() + 1 : 0;
    const withholdingTax = Number(form2307Row.wtax ?? calculations.wtax ?? 0);
    const transactionAmount = Number(form2307Row.gross_amount ?? calculations.grossCommission ?? 0);

    return {
      startDate: formatForm2307Date(quarterStart),
      endDate: formatForm2307Date(quarterEnd),
      payeeTin: String(form2307Row.agent_tin_number || '').trim(),
      payeeName: String(form2307Row.agent_name || ''),
      payeeAddress: String(form2307Row.branch_address || form2307Row.branch || ''),
      payeeZipcode: '',
      corpTin: FORM2307_PAYOR_DEFAULTS.corpTin,
      corpName: FORM2307_PAYOR_DEFAULTS.corpName,
      corpAddress: FORM2307_PAYOR_DEFAULTS.corpAddress,
      corpZipcode: FORM2307_PAYOR_DEFAULTS.corpZipcode,
      monthOnly,
      totalWithholdingTax: withholdingTax,
      rows: [
        {
          income_label: 'Income payments made by top 10,000 private corporations to their local/resident suppliers of services - Corporate',
          atc: resolveAtcCode(transactionAmount, withholdingTax, form2307Row.transaction_tag),
          transaction_amount: transactionAmount,
          withholding_tax: withholdingTax,
          month_only: monthOnly,
        },
      ],
      repName: FORM2307_PAYOR_DEFAULTS.repName,
      repPosition: FORM2307_PAYOR_DEFAULTS.repPosition,
      repTin: FORM2307_PAYOR_DEFAULTS.repTin,
      signatureImageUrl: '',
    };
  }, [form2307Row, resolveCommissionRate]);

  function handleDownload2307() {
    if (!form2307Data) {
      return;
    }

    const sheet = document.getElementById('findesk-form-2307-sheet');
    if (!sheet) {
      return;
    }

    const printWindow = window.open('', '_blank', 'width=1400,height=980');
    if (!printWindow) {
      window.alert('Unable to open Form 2307 window. Please allow pop-ups for this site and try again.');
      return;
    }

    const styleTags = Array.from(document.querySelectorAll('style, link[rel="stylesheet"]'))
      .map((node) => node.outerHTML)
      .join('\n');
    const safeAgentName = String(form2307Data.payeeName || 'agent')
      .replace(/[^a-z0-9_-]+/gi, '_')
      .replace(/^_+|_+$/g, '')
      || 'agent';

    printWindow.document.open();
    printWindow.document.write(`<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="utf-8" />
  <meta name="viewport" content="width=device-width,initial-scale=1" />
  <title>2307_${safeAgentName}_${todayIso()}</title>
  ${styleTags}
</head>
<body class="fd2307-print-window">
  <div class="fd2307-print-window-wrap">
    ${sheet.outerHTML}
  </div>
</body>
</html>`);
    printWindow.document.close();
    printWindow.focus();

    const runPrint = () => {
      try {
        printWindow.print();
      } catch (error) {
        // no-op
      }
    };

    printWindow.addEventListener('load', () => {
      setTimeout(runPrint, 250);
    });
    setTimeout(runPrint, 900);
  }

  return (
    <div className="dashboard-container">
      <section className="dashboard-header">
        <div>
          <h1>Commissions</h1>
          <p>Monitor released, pending, and auto-deducted commissions from paid collections.</p>
        </div>
        <div className="commissions-header-actions">
          <div className="commissions-branch-filter">
            <label htmlFor="commissions-branch">Branch</label>
            <select
              id="commissions-branch"
              value={selectedBranch}
              onChange={(event) => setSelectedBranch(event.target.value)}
              disabled={!isAdmin}
            >
              {isAdmin ? <option value="all">All Branches</option> : null}
              {branchOptions.map((branchName) => (
                <option key={branchName} value={branchName}>
                  {branchName}
                </option>
              ))}
            </select>
          </div>
          <button type="button" className="profile-secondary-btn" onClick={exportCsv}>
            Export CSV
          </button>
          <button type="button" className="profile-secondary-btn" onClick={exportExcel}>
            Export Excel
          </button>
          {canRelease && selectedCollectionIds.length > 0 ? (
            <button
              type="button"
              className="profile-secondary-btn"
              onClick={() => setReleaseDialog(true)}
              disabled={isSubmitting}
            >
              Release ({selectedCollectionIds.length})
            </button>
          ) : null}
        </div>
      </section>

      <section className="dashboard-content commissions-page-content">
        {error ? <div className="error-box">{error}</div> : null}
        {message ? <div className="success-box">{message}</div> : null}

        <div className="commissions-summary-grid">
          <div className="commissions-summary-card"><p>Total Gross</p><strong>{formatCurrency(summary.totalGross)}</strong></div>
          <div className="commissions-summary-card"><p>Total W/Tax</p><strong>{formatCurrency(summary.totalWtax)}</strong></div>
          <div className="commissions-summary-card"><p>Total Net</p><strong>{formatCurrency(summary.totalNet)}</strong></div>
          <div className="commissions-summary-card is-good"><p>Released</p><strong>{formatCurrency(summary.totalReleased)}</strong></div>
          <div className="commissions-summary-card is-warning"><p>Pending Release</p><strong>{formatCurrency(summary.totalPending)}</strong></div>
        </div>

        <div className="commissions-filter-bar">
          <div className="commissions-filter-fields">
            <input type="text" placeholder="Search by transaction, agent, client, product..." value={search} onChange={(event) => setSearch(event.target.value)} />
            <input type="date" value={dateFrom} onChange={(event) => setDateFrom(event.target.value)} />
            <input type="date" value={dateTo} onChange={(event) => setDateTo(event.target.value)} />
            <select value={statusFilter} onChange={(event) => setStatusFilter(event.target.value)}>
              <option value="all">All Status</option>
              <option value="Pending">Pending</option>
              <option value="Paid">Paid (Tagged)</option>
              <option value="Released">Released</option>
            </select>
          </div>
          <div className="commissions-filter-actions">
            <button type="button" className="profile-secondary-btn" onClick={clearFilters}>
              Clear
            </button>
          </div>
        </div>

        <div className="commissions-tabs">
          {TAB_ITEMS.map((tab) => (
            <button
              key={tab.key}
              type="button"
              className={`commissions-tab-btn ${activeTab === tab.key ? 'is-active' : ''}`}
              onClick={() => setActiveTab(tab.key)}
            >
              {tab.label}
            </button>
          ))}
        </div>

        <CommissionTable
          rows={pagination.pagedItems}
          activeTab={activeTab}
          selectedCollectionIds={selectedCollectionIds}
          pendingCollectionIds={pendingCollectionIds}
          allPendingSelected={allPendingSelected}
          isLoading={isLoading}
          resolveCommissionRate={resolveCommissionRate}
          resolveRowStatus={resolveRowStatus}
          onToggleRowSelection={toggleRowSelection}
          onToggleAllPendingSelection={toggleAllPendingSelection}
          onOpenForm2307={setForm2307Row}
          onOpenTagPaid={(row) => {
            setPayRow(row);
            // Prefill from an existing payout so an admin can review/edit it.
            const existing = row?.agent_payout || null;
            setPayForm({
              reference_number: existing?.reference_number || '',
              payment_date: existing?.payment_date || todayIso(),
              proof_of_payment_name: existing?.proof_of_payment_name || '',
              proof_of_payment_url: existing?.proof_of_payment_url || '',
            });
          }}
          onOpenCancel={(row) => {
            setCancelRow(row);
            setCancelRemarks('');
          }}
          canTagPaid={canTagPaid}
          canCancel={canCancel}
          canRelease={canRelease}
        />

        {!isLoading ? <TablePagination {...pagination} /> : null}
      </section>

    <CommissionModals
        payRow={payRow}
        payForm={payForm}
        isSubmitting={isSubmitting}
        onPayFormChange={updatePayForm}
        onUploadProof={handleUploadCommissionProof}
        onClosePay={() => setPayRow(null)}
        onSubmitPay={submitTagPaid}
        cancelRow={cancelRow}
        cancelRemarks={cancelRemarks}
        onCancelRemarksChange={setCancelRemarks}
        onCloseCancel={() => setCancelRow(null)}
        onSubmitCancel={submitCancelCommission}
        releaseDialog={releaseDialog}
        releaseForm={releaseForm}
        selectedCount={selectedCollectionIds.length}
        releaseTotal={releaseTotal}
        onReleaseFormChange={updateReleaseForm}
        onCloseRelease={() => setReleaseDialog(false)}
        onSubmitRelease={submitBulkRelease}
        form2307Row={form2307Row}
        form2307Data={form2307Data}
        onClose2307={() => setForm2307Row(null)}
        onDownload2307={handleDownload2307}
        resolveCommissionRate={resolveCommissionRate}
        canTagPaid={canTagPaid}
        canCancel={canCancel}
        canRelease={canRelease}
      />
    </div>
  );
}
